import React from "react";
import {
  isDownloadableContentType,
  isFileDownloadAllowed,
  isFilePrintAllowed,
  isPrintableContentType,
  isVisibleToStudents,
  type SetViewerOption,
} from "../../schemas/book-schemas";

interface ViewerOptionTogglesProps {
  sessionId: string;
  /** undefined for a section's single content_block, index for merged content_blocks */
  blockIndex?: number;
  block?: { content_type?: string; payload?: any } | null;
  onSetViewerOption?: SetViewerOption;
}

const pillClass = (on: boolean, onClass: string) =>
  `flex items-center h-9 px-3 rounded-lg border text-xs font-medium cursor-pointer select-none transition-colors ${
    on
      ? onClass
      : "bg-white border-gray-200 text-gray-600 hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-400"
  }`;

const checkboxClass =
  "h-3.5 w-3.5 mr-2 text-blue-600 focus:ring-blue-500 border-gray-300 rounded";

/**
 * The `payload.viewer` switches shown in a block header. Rendered by every
 * block kind — file blocks through ContentFile, the rest through
 * ContentBlock — so hiding a block from students does not depend on which
 * renderer happens to draw it.
 */
export const ViewerOptionToggles: React.FC<ViewerOptionTogglesProps> = ({
  sessionId,
  blockIndex,
  block,
  onSetViewerOption,
}) => {
  if (!onSetViewerOption) {
    return null;
  }

  // Bài kiểu cũ giữ file ở `content.file` và không có khối nội dung, nên không
  // có chỗ nào mang cờ. Hiện công tắc mờ kèm lý do thay vì bỏ đi không nói gì.
  if (!block) {
    return (
      <span
        className="flex items-center h-9 px-3 rounded-lg border text-xs font-medium select-none bg-gray-50 border-gray-200 text-gray-400 cursor-not-allowed dark:bg-gray-800 dark:border-gray-700 dark:text-gray-500"
        title="Bài này còn ở định dạng cũ nên chưa đặt được hiển thị với học sinh. Tải lại file cho bài để chuyển sang định dạng mới."
        data-testid="student-visibility-unavailable"
      >
        Hiển thị với học sinh
      </span>
    );
  }

  const visibleToStudents = isVisibleToStudents(block);
  const printAllowed = isFilePrintAllowed(block);
  const downloadAllowed = isFileDownloadAllowed(block);
  const canTogglePrint = isPrintableContentType(block.content_type);
  const canToggleDownload = isDownloadableContentType(block.content_type);

  return (
    <>
      <label
        // Hidden blocks read as a warning: the reader never receives them
        className={pillClass(
          !visibleToStudents,
          "bg-amber-50 border-amber-200 text-amber-700 dark:bg-amber-900/30 dark:border-amber-700 dark:text-amber-300"
        )}
        title={
          visibleToStudents
            ? "Học sinh thấy nội dung này"
            : "Chỉ giáo viên thấy nội dung này, học sinh không nhận được"
        }
        data-testid="student-visibility-toggle"
      >
        <input
          type="checkbox"
          className={checkboxClass}
          checked={visibleToStudents}
          onChange={(e) =>
            onSetViewerOption(sessionId, blockIndex, "visible_to_students", e.target.checked)
          }
        />
        {visibleToStudents ? "Hiển thị với học sinh" : "Ẩn với học sinh"}
      </label>
      {canTogglePrint && (
        <label
          className={pillClass(
            printAllowed,
            "bg-blue-50 border-blue-200 text-blue-700 dark:bg-blue-900/30 dark:border-blue-700 dark:text-blue-300"
          )}
          title={printAllowed ? "Người đọc được in file này" : "Người đọc không được in file này"}
          data-testid="file-print-toggle"
        >
          <input
            type="checkbox"
            className={checkboxClass}
            checked={printAllowed}
            onChange={(e) => onSetViewerOption(sessionId, blockIndex, "allow_print", e.target.checked)}
          />
          Cho phép in
        </label>
      )}
      {canToggleDownload && (
        <label
          className={pillClass(
            downloadAllowed,
            "bg-blue-50 border-blue-200 text-blue-700 dark:bg-blue-900/30 dark:border-blue-700 dark:text-blue-300"
          )}
          title={
            downloadAllowed
              ? "Người đọc được lưu file này về máy"
              : "Người đọc không được lưu file này về máy"
          }
          data-testid="file-download-toggle"
        >
          <input
            type="checkbox"
            className={checkboxClass}
            checked={downloadAllowed}
            onChange={(e) =>
              onSetViewerOption(sessionId, blockIndex, "allow_download", e.target.checked)
            }
          />
          Cho phép tải
        </label>
      )}
    </>
  );
};
